import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { selectionSet, type BaseQLSelectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { AdornisTemplater } from '@adornis/templater/server/index.js';
import { Language } from '@adornis/translation-core/languages.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { Router } from 'express';
import { DateTime } from 'luxon';
import { ParticipationState } from '../enums/enums.js';
import { NoParticipantsAvailableError } from '../errors/NoParticipantsAvailableError.js';
import { notifyNuzterTeilnahme } from '../notification/nutzer-teilnahme.js';
import { at, isDefined } from '../util.js';
import { Participation } from './Participation.js';
import { TextMultipleLangItem } from './TextMultipleLangItem.js';
import { Appointment } from './appointment.js';
import { EinzelberatungSlot, type Einzelberatung } from './einzelberatung.js';
import { Institution } from './institution.js';
import { InstitutionMailTemplate, MailTemplateType } from './mail-templates.js';
import { buildifyHtmlToReadableText, CertificateAttachedProduct, Product } from './products.js';
import { User } from './user.js';
import { Workshop } from './workshop.js';

@Entity()
export class CertificateFile extends AdornisFile {
  static override _class = 'CertificateFile';

  @validate(nonOptional())
  @Field(type => ID)
  participationID!: string;
}

export enum CertificateVariable {
  User_Vorname = 'User_Vorname',
  User_Nachname = 'User_Nachname',
  User_Geburtstag = 'User_Geburtstag',
  Produkt_Titel = 'Produkt_Titel',
  Produkt_Beschreibung_DE = 'Produkt_Beschreibung_DE',
  Produkt_Beschreibung_EN = 'Produkt_Beschreibung_EN',
  Zertifikat_Beschreibung_DE = 'Zertifikat_Beschreibung_DE',
  Zertifikat_Beschreibung_EN = 'Zertifikat_Beschreibung_EN',
  Termine_Datum_Uhrzeit = 'Termine_Datum_Uhrzeit',
  Termine_Datum_Uhrzeit_EN = 'Termine_Datum_Uhrzeit_EN',
  Erstellungs_Datum = 'Erstellungs_Datum',
  Zeitraum = 'Zeitraum',
  Startdatum = 'Startdatum',
  Enddatum = 'Enddatum',
}

@Entity()
export class Certificate extends MongoEntity {
  public static override _class = 'Certificate';
  public static override _collectionName = 'certificates';

  static override get allFields() {
    return selectionSet(() => this, 3);
  }

  @Field(type => String)
  @validate(nonOptional())
  title!: string;

  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  createdAt!: DateTime;

  @Field(type => String)
  @validate(nonOptional())
  text!: string;

  @Field(type => String)
  fileID!: string;

  @Field(type => AdornisFile, {
    resolve: function (this: Certificate) {
      return async gqlFields => (await getCollection(AdornisFile._class)).findOne<AdornisFile>({ _id: this.fileID });
    },
  })
  file: Maybe<AdornisFile>;

  @validate(nonOptional())
  @Field(type => String)
  public institutionID!: string;

  override toString() {
    return this.title ?? '';
  }

  @Query(type => [Certificate])
  static getCertificatesByInstitution(@Arg('institutionID', type => ID) institutionID: ID) {
    return async (gqlFields: BaseQLSelectionSet<Certificate>) => {
      const collection = await getCollection<Certificate>(Certificate._class);
      const result = await collection.find<Certificate>({ institutionID }).toArray();
      return result;
    };
  }

  @Query(type => Int)
  static sendCertificateToParticipant(@Arg('participationID', type => ID) participationID: string) {
    return async () => {
      const participation = await getByID(
        Participation,
        participationID,
      )({
        productID: 1,
      });
      if (!participation) throw new Error('Cannot find participation for ID ' + participationID);

      const product = await getByID(
        Product,
        participation.productID,
      )({
        _id: 1,
        institutionID: 1,
        attachedCertificate: {
          exportDate: 1,
          certificateID: 1,
        },
        slots: EinzelberatungSlot.allFields,
      });
      if (!product) throw new Error('Cannot find product for ID ' + participation.productID);
      if (!product.attachedCertificate?.certificateID) throw new Error('cert_gen_failed_missing_cert');

      // check if sending mails is enabled
      const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
        product.institutionID,
        MailTemplateType.NUTZER_TEILNAHME,
      )(InstitutionMailTemplate.allFields);

      if (!mailTemplate.whatsappNotification && !mailTemplate.emailNotification) {
        throw new Error('cert_mails_not_enabled');
      }

      await notifyNuzterTeilnahme(participationID, true);

      return 1;
    };
  }

  @Mutation(type => Int)
  static sendCertificateToProductParticipantsWhichHaveParticipated(@Arg('productID', type => ID) productID: string) {
    return async () => {
      const product = await getByID(
        Product,
        productID,
      )({
        _id: 1,
        name: 1,
        appointments: Appointment.allFields,
        capacity: 1,
        attachedCertificate: {
          exportDate: 1,
          certificateID: 1,
        },
      });
      if (!product) throw new Error('Cannot find product for ID ' + productID);

      const allParticipations = await (await getCollection<Participation>(Participation._class))
        .find({ productID, state: ParticipationState.PARTICIPATED })
        .toArray();
      if (!allParticipations.length) throw new NoParticipantsAvailableError();

      const participationWithErrors = [] as string[];
      for (const participation of allParticipations) {
        try {
          await this.sendCertificateToParticipant(participation._id)();
        } catch (e) {
          logger.error(e);
          participationWithErrors.push(participation._id);
        }
      }

      if (!product.attachedCertificate) {
        throw new Error('Cannot send certificate without attached certificate');
      }

      product.attachedCertificate.exportDate = DateTime.now();
      await product.save();

      if (participationWithErrors.length) {
        throw new Error('Could not send mail to ' + participationWithErrors.length + ' participants');
      }

      return 1;
    };
  }

  static async generateCertificateFileFromParticipation(
    product: NarrowedEntity<
      Product,
      {
        _id: 1;
        name: 1;
        attachedCertificate: CertificateAttachedProduct.allFields;
        translatedDescriptionHTML: TextMultipleLangItem.allFields;
        appointnents: Appointment.allFields;
      }
    >,
    user: NarrowedEntity<
      User,
      {
        _id: 1;
        firstName: 1;
        title: 1;
        lastName: 1;
        dateOfBirth: 1;
        email: 1;
        roles: AdornisRoleToContextsWrapper.allFields;
      }
    >,
  ) {
    if (!product.attachedCertificate?.certificateID)
      throw new Error('Cannot generate certificate for workshop without linked certificate');

    const certificateTemplate = await getByID(this, product.attachedCertificate.certificateID)(this.allFields);
    if (!certificateTemplate)
      throw new Error('No certificate found for ID ' + product.attachedCertificate.certificateID);

    const certificateTemplateFile = certificateTemplate.file;
    if (!certificateTemplateFile) throw new Error('Certificate has no file attached');

    const participation = await loadMongoData(Participation, { productID: product._id, userID: user._id }).then(at(0));
    if (!participation) throw new Error('Participation not found');

    // drop existing certificate (when it exists)
    const existingCertificateFileData = await loadMongoData(CertificateFile, {
      participationID: participation._id,
    }).then(at(0));

    const existingCertificateFile = existingCertificateFileData
      ? await getByID(CertificateFile, existingCertificateFileData._id)({ _id: 1 })
      : null;
    await existingCertificateFile?.remove();

    const pdfBuffer = await this.renderTemplate(certificateTemplateFile, product, user);
    const file = (await CertificateFile.createFromBuffer(pdfBuffer, 'certificate.pdf', {
      participationID: participation._id,
    })) as CertificateFile;

    await file.save();

    return file;
  }

  private static renderTemplate(
    certificateTemplateFile: CertificateFile,
    product: NarrowedEntity<
      Product,
      {
        _id: 1;
        name: 1;
        attachedCertificate: CertificateAttachedProduct.allFields;
        translatedDescriptionHTML: TextMultipleLangItem.allFields;
        appointnents: Appointment.allFields;
      }
    >,
    user: NarrowedEntity<
      User,
      {
        _id: 1;
        firstName: 1;
        title: 1;
        lastName: 1;
        email: 1;
        dateOfBirth: 1;
        roles: AdornisRoleToContextsWrapper.allFields;
      }
    >,
  ) {
    const variables = this._fillVariablesForCertificate(product, user);
    const templater = AdornisTemplater.fromFile(certificateTemplateFile.getFullPath());
    templater.render(variables);

    return templater.toPdfBuffer();
  }

  private static _fillVariablesForCertificate(
    product: NarrowedEntity<
      Product,
      {
        name: 1;
        attachedCertificate: CertificateAttachedProduct.allFields;
        translatedDescriptionHTML: TextMultipleLangItem.allFields;
        appointnents: Appointment.allFields;
      }
    >,
    user: NarrowedEntity<
      User,
      { title: 1; firstName: 1; lastName: 1; email: 1; dateOfBirth: 1; roles: AdornisRoleToContextsWrapper.allFields }
    >,
  ) {
    // Extract appointment dates for new variables
    const startDate =
      product instanceof Workshop && product.appointments?.length > 0
        ? product.appointments
            .map(ap => ap.start)
            .filter(isDefined)
            .sort((a, b) => a.toMillis() - b.toMillis())
            .at(0)
            ?.toFormat('dd.LL.yyyy')
        : (product as Einzelberatung).slotConfig?.start.toFormat('dd.LL.yyyy');

    const endDate =
      product instanceof Workshop && product.appointments?.length > 0
        ? product.appointments
            .map(ap => ap.end)
            .filter(isDefined)
            .sort((a, b) => b.toMillis() - a.toMillis())
            .at(0)
            ?.toFormat('dd.LL.yyyy')
        : (product as Einzelberatung).slotConfig?.end.toFormat('dd.LL.yyyy');

    const zeitraum = (() => {
      if (!startDate || !endDate) return '';
      if (!startDate && endDate) return endDate;
      if (startDate && !endDate) return startDate;
      if (startDate === endDate) return startDate;
      return `${startDate} - ${endDate}`;
    })();

    function termineDatumUhrzeit(locale: string) {
      return product instanceof Workshop
        ? (() => {
            const weekdays = product.appointments.map(ap => ap.start?.toFormat('ccc', { locale })).filter(isDefined);
            const dates = product.appointments.map(ap => ap.start?.toFormat('dd.LL.yyyy')).filter(isDefined);
            const times = product.appointments
              .map(
                ap =>
                  ap.start?.toFormat('HH:mm') +
                  ' - ' +
                  (locale === 'de' ? ap.end?.toFormat('HH:mm') + ' Uhr' : ap.end?.toFormat('HH:mm a')),
              )
              .filter(isDefined);

            const dayString = weekdays.every(wd => wd === weekdays[0]) ? weekdays[0] : weekdays.join('/');
            const datesString = dates.every(d => d === dates[0]) ? dates[0] : dates.join(' & ');
            const timesString = times.every(t => t === times[0]) ? times[0] : times.join('/');
            return `${dayString}, ${datesString} (${timesString})`;
          })()
        : (product as Einzelberatung).slotConfig?.start.toFormat('dd.LL.yyyy');
    }

    return {
      [CertificateVariable.User_Vorname]: user.firstName,
      [CertificateVariable.User_Nachname]: user.lastName,
      [CertificateVariable.Produkt_Titel]: product.name,
      [CertificateVariable.User_Geburtstag]: user.dateOfBirth?.toFormat('dd.LL.yyyy'),
      [CertificateVariable.Produkt_Beschreibung_DE]: buildifyHtmlToReadableText(
        TextMultipleLangItem.findByLanguage(product.translatedDescriptionHTML ?? [], Language.GERMAN)?.text ?? '',
      ),
      [CertificateVariable.Produkt_Beschreibung_EN]: buildifyHtmlToReadableText(
        TextMultipleLangItem.findByLanguage(product.translatedDescriptionHTML ?? [], Language.ENGLISH)?.text ?? '',
      ),
      [CertificateVariable.Zertifikat_Beschreibung_DE]: buildifyHtmlToReadableText(
        TextMultipleLangItem.findByLanguage(product.attachedCertificate?.descriptionsHTML ?? [], Language.GERMAN)
          ?.text ?? '/',
      ),
      [CertificateVariable.Zertifikat_Beschreibung_EN]: buildifyHtmlToReadableText(
        TextMultipleLangItem.findByLanguage(product.attachedCertificate?.descriptionsHTML ?? [], Language.ENGLISH)
          ?.text ?? '/',
      ),
      [CertificateVariable.Erstellungs_Datum]: product.getLastExportDate,
      [CertificateVariable.Startdatum]: startDate ?? '',
      [CertificateVariable.Enddatum]: endDate ?? '',
      [CertificateVariable.Zeitraum]: zeitraum,
      [CertificateVariable.Termine_Datum_Uhrzeit]: termineDatumUhrzeit(Language.GERMAN) ?? '',
      [CertificateVariable.Termine_Datum_Uhrzeit_EN]: termineDatumUhrzeit(Language.ENGLISH) ?? '',
    };
  }

  @Query(type => String)
  static getFileIDByCertificateID(@Arg('certificateID', type => String) certificateID: string) {
    return async () => {
      const collection = await getCollection<Certificate>(Certificate._class);
      const result = await collection.findOne<Certificate>({ _id: certificateID });
      return result?.fileID;
    };
  }

  static showPreview(fileID: string) {
    return A.absoluteUrl(`/api/certificate-preview/${fileID}`);
  }

  static previewRouter() {
    const router = Router();
    router.use('/api/certificate-preview', async (req, res, next) => {
      const fileID = req.path.split('/')[1];
      if (!fileID) {
        logger.error('No fileID given for certificate preview');
        return res.sendStatus(422);
      }

      const certificateFile = await AdornisFile.getByID<CertificateFile>(fileID)(AdornisFile.allFields);
      if (!certificateFile) {
        logger.error('Could not find certificateFile for ID ' + fileID);
        return res.sendStatus(422);
      }

      const musterWorkshop = new Workshop({
        name: 'Muster-Workshop',
        description:
          'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec posuere pulvinar urna, eu tincidunt ipsum. Maecenas a finibus elit. Nullam tincidunt pharetra ornare. Donec sit amet lorem pellentesque, imperdiet purus ut, faucibus ipsum. Vestibulum interdum nunc at magna dapibus, eget malesuada nisl commodo. Vivamus vel ipsum eros. In finibus rutrum nunc, et consectetur sem imperdiet ut. Aliquam rhoncus, lorem sit amet eleifend egestas, eros tortor vehicula velit, ultrices vehicula lorem magna sed nisl. Sed vehicula turpis in leo placerat consectetur. Praesent et placerat augue, vel efficitur est. Etiam felis tellus, aliquet ac fermentum sit amet, consectetur a magna. Aliquam nibh felis, luctus sed nunc ut, convallis auctor urna. Praesent vel libero vestibulum, efficitur erat non, egestas ex.',
        attachedCertificate: new CertificateAttachedProduct({
          descriptionsHTML: [
            new TextMultipleLangItem({
              langKey: Language.GERMAN,
              text: 'Zertifikat Beschreibung de',
            }),
            new TextMultipleLangItem({
              langKey: Language.ENGLISH,
              text: 'Zertifikat Beschreibung en',
            }),
          ],
        }),
        appointments: [
          new Appointment({
            start: DateTime.now().minus({ days: 2, hours: 2 }),
            end: DateTime.now().minus({ days: 2 }),
          }),
          new Appointment({
            start: DateTime.now().minus({ days: 1, hours: 2 }),
            end: DateTime.now().minus({ days: 1 }),
          }),
        ],
      });

      try {
        const fileBuffer = await Certificate.renderTemplate(
          certificateFile,
          musterWorkshop,
          new User({
            email: 'max@mustermann.de',
            firstName: 'Max',
            lastName: 'Mustermann',
            dateOfBirth: new Date(),
          }),
        );

        res.setHeader('content-type', 'application/pdf');
        return res.end(fileBuffer);
      } catch (e) {
        logger.warn(e);
        return res.status(500).send('could not generate certificate');
      }
    });
    return router;
  }

  static async getAllowedCertificates() {
    // check permissions
    const userID = context.serverContext ? undefined : (context.userID as string | undefined);
    if (!userID) return [];
    const user = context.serverContext
      ? undefined
      : await CurrentUserInfo.getMyself<User>()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!user) return [];

    const institutionIDs = await Institution.getAllowedInstitutions(true);

    const result = await (
      await getRawCollection(Certificate._collectionName)
    )
      .aggregate<{ _id: string }>([
        {
          $match: {
            institutionID: {
              $in: institutionIDs,
            },
          },
        },
        {
          $project: {
            _id: 1,
          },
        },
      ])
      .toArray();

    return result.map(res => res._id);
  }
}
